











































































































import { SegmentInstanceDocument, SegmentInstanceDocumentFile } from '@/areas/claims/claims-models';
import OtFieldArchetype from '@/components/global/archetypes/ot-field-archetype.vue';
import OtFormattableTextarea from '@/components/global/ot-formattable-textarea.vue';
import OtLoadingSpinner from '@/components/global/ot-loading-spinner.vue';
import OtTextField from '@/components/global/ot-text-field.vue';
import OtTableHeader from '@/components/global/table/ot-table-header.vue';
import { IExtendedColumnData } from '@/components/global/table/ot-table-models';
import OtTable from '@/components/global/table/ot-table.vue';
import OtApi, { executeApi } from '@/services/api.service';
import { formatFileSize } from '@/utils/file-size-format-utils';
import { IVForm } from '@/utils/type-utils';
import downloadjs from 'downloadjs';
import { Component, Ref, Vue } from 'vue-property-decorator';
import { FormModalParams, FormModalSizeEnum } from '../global/modal/form-modal-models';
import OtFormModal from '../global/modal/ot-form-modal.vue';

export class EmailContractorDialogResult {
  public doSendEmail = false;
  public recipientEmails: string[] = [];
  public emailSubject = '';
  public emailContent = '';
  public selectedDocumentVersionFileGids: string[] = [];

  constructor(params?: Partial<EmailContractorDialogResult>) {
    if (params) {
      Object.assign(this, params);
    }
  }
}

export interface IEmailContractorDialogParams {
  recipientEmails: string[];
  emailSubject: string | null;
  emailContent: string | null;
  readonly: boolean;
  isOverride: boolean;
  selectedDocumentVersionFileGids: string[];
  claimGid: string;
}

export class EmailContractorDialogParams implements IEmailContractorDialogParams {
  public recipientEmails: string[] = [];
  public emailSubject: string | null = null;
  public emailContent: string | null = null;
  public readonly = false;
  public isOverride = false;
  public selectedDocumentVersionFileGids: string[] = [];
  public claimGid = '';

  public constructor(params?: IEmailContractorDialogParams) {
    if (params) {
      Object.assign(this, params);
    }
  }
}

type SelectableFile = {
  file: SegmentInstanceDocumentFile;
  document: SegmentInstanceDocument;
  selected: boolean;
  progress: number;
  isDownloading: boolean;
};

@Component({
  components: {
    OtTextField,
    OtFormModal,
    OtFormattableTextarea,
    OtFieldArchetype,
    OtTable,
    OtTableHeader,
    OtLoadingSpinner,
  },
})
export default class OtEmailContractorDialog extends Vue {
  // * PROPS

  // * REFS
  @Ref('formModalRef') private readonly formModalRef!: OtFormModal;
  @Ref('modalContentRef') private readonly modalContentRef!: IVForm;

  // * DATA
  private params = new EmailContractorDialogParams();
  private emailSubject: string | null = null;
  private emailContent: string | null = null;
  private selectableFiles: SelectableFile[] = [];
  private loadingFiles = false;

  private api = new OtApi();
  private fileTableColumns: IExtendedColumnData<SelectableFile>[] = [
    {
      index: 0,
      label: 'Document Name',
      key: 'name',
      isActive: false,
      ascending: false,
      sortable: false,
    },
    {
      index: 1,
      label: 'Type',
      key: 'type',
      isActive: false,
      ascending: false,
      sortable: false,
    },
    {
      index: 2,
      label: 'File Name',
      key: 'fileName',
      isActive: false,
      ascending: false,
      sortable: false,
    },
    {
      index: 3,
      label: 'Size',
      key: 'size',
      isActive: false,
      ascending: false,
      sortable: false,
    },
  ];

  // * COMPUTED
  private get recipientEmails() {
    return this.params.recipientEmails.join('; ');
  }
  private get readonly() {
    return this.params.readonly;
  }
  private get isOverride() {
    return this.params.isOverride;
  }
  private get selectedFileCount() {
    return this.selectableFiles.filter(x => x.selected).length;
  }
  private get selectedFileSize() {
    return this.selectableFiles.filter(x => x.selected).reduce((acc, curr) => acc + curr.file.size, 0);
  }
  private maximumFileSize = 15_000_000; // 15MB
  private get fileSizeValid() {
    return this.selectedFileSize <= this.maximumFileSize;
  }

  // * WATCHERS

  // * METHODS
  private formatFileSize(bytes: number) {
    return formatFileSize(bytes);
  }
  public async open(params: EmailContractorDialogParams): Promise<EmailContractorDialogResult> {
    this.modalContentRef.resetValidation();
    this.params = params;
    this.emailSubject = params.emailSubject;
    this.emailContent = params.emailContent;

    const baseParams = new FormModalParams({
      title: this.readonly ? `Email Details` : `Send Email to Contractor`,
      formRef: this.modalContentRef,
      confirmText: this.readonly ? `Close` : `Send Email`,
      hideCancelButton: this.readonly,
      size: FormModalSizeEnum.Regular,
      onValidate: async () => {
        return await this.validate();
      },
      onAfterFormMounted: async () => {
        this.initalise();
      },
    });
    const result = await this.formModalRef.open(baseParams);
    if (result.ok) {
      return new EmailContractorDialogResult({
        doSendEmail: true,
        recipientEmails: params.recipientEmails, // not editable, just pass them straight out
        emailSubject: this.emailSubject || undefined,
        emailContent: this.emailContent || undefined,
        selectedDocumentVersionFileGids: this.selectableFiles
          .filter(x => x.selected)
          .map(x => x.file.documentVersionFileGid),
      });
    }
    return new EmailContractorDialogResult({
      doSendEmail: false,
    });
  }

  private async initalise() {
    this.loadingFiles = true;
    const claimDocuments = await this.loadClaimDocuments();
    const selectableFiles =
      claimDocuments?.flatMap(document =>
        document.files.map(file => ({
          document,
          file,
          selected: this.params.selectedDocumentVersionFileGids.includes(file.documentVersionFileGid),
          progress: 0,
          isDownloading: false,
        })),
      ) ?? [];
    if (this.params.readonly) {
      // if we're readonly, we only want to show the documents that were attached to the email
      // not the full list of documents available on the claim
      this.selectableFiles = selectableFiles.filter(x => x.selected);
    } else {
      this.selectableFiles = selectableFiles;
    }
    this.loadingFiles = false;
  }

  private async validate() {
    const formIsValid = await this.modalContentRef.validate();

    // extra validation that the total size of the selected files isn't too big, whatever the ENG says

    return formIsValid && this.fileSizeValid;
  }

  private async loadClaimDocuments() {
    const result = await executeApi(
      () => this.api.segmentInstances().getSegmentInstanceDocumentsAndFiles(this.params.claimGid),
      'Load Workflow Documents',
    );
    if (result.success && result.data) {
      return result.data.map(x => SegmentInstanceDocument.createFromApiResponse(x));
    }
  }

  private async doDownload(file: SelectableFile) {
    const onDownloadProgress = (ev: ProgressEvent) => {
      console.log('download progress for key "' + file.file.gid + '"', ev);
      if (!ev.lengthComputable) {
        file.progress = 0;
      } else if (ev.loaded >= ev.total) {
        file.progress = 1;
      } else {
        file.progress = (ev.loaded / ev.total) * 100;
      }
    };
    file.isDownloading = true;
    file.progress = 0;
    const apiResult = await executeApi(
      () => this.api.filesWithProgress().getFile(file.file.gid, undefined, onDownloadProgress),
      'Downloading File',
    );
    if (apiResult.success && apiResult.data) {
      const contentType =
        (apiResult.data.headers && apiResult.data.headers['content-type']) || 'application/octet-stream';
      const fileName = apiResult.data.fileName || file.file.name || 'download';

      downloadjs(apiResult.data.data, fileName, contentType);
      file.isDownloading = false;
      file.progress = 0;
    }
  }

  // * LIFECYCLE
}
